import React from 'react';

// Same palette as backend for fallback determinism
const PALETTE = [
  '#D93025', '#0EA5E9', '#7C3AED', '#059669', '#EA580C', '#DB2777',
  '#CA8A04', '#0D9488', '#4338CA', '#65A30D', '#B45309', '#9333EA',
];

const sizeMap = {
  xs: 'w-5 h-5 text-[10px]',
  sm: 'w-6 h-6 text-[10px]',
  md: 'w-8 h-8 text-xs',
  lg: 'w-10 h-10 text-sm',
  xl: 'w-16 h-16 text-2xl',
  xxl: 'w-24 h-24 text-4xl',
};

// Deterministic fallback color when user.avatar_color is missing
const hashColor = (seed) => {
  if (!seed) return PALETTE[0];
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

/**
 * UserAvatar — renders profile picture if `avatar_url` is present,
 * otherwise a colored circle with the user's initial(s).
 *
 * Props:
 *   user        — { id, name, avatar_color?, avatar_url? } | string (just a name)
 *   size        — xs | sm | md | lg | xl | xxl  (default: md)
 *   className   — extra classes appended to the wrapper
 *   ring        — render a thin white ring (useful for avatar stacks)
 */
const UserAvatar = ({ user, size = 'md', className = '', ring = false, title, ...rest }) => {
  if (!user) return null;
  const isObj = typeof user === 'object';
  const name = isObj ? (user.name || '') : String(user);
  const color = (isObj && user.avatar_color) || hashColor(isObj ? (user.id || user.email || name) : name);
  const avatarUrl = isObj ? user.avatar_url : null;
  const getInitials = (fullName) => {
    const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  };
  const initial = getInitials(name);

  const dim = sizeMap[size] || sizeMap.md;
  const ringClass = ring ? 'ring-2 ring-white' : '';
  const titleAttr = title || name;

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        title={titleAttr}
        className={`${dim} ${ringClass} ${className} rounded-full object-cover flex-shrink-0`}
        {...rest}
      />
    );
  }

  return (
    <div
      title={titleAttr}
      className={`${dim} ${ringClass} ${className} rounded-full flex items-center justify-center font-bold text-white flex-shrink-0`}
      style={{ backgroundColor: color }}
      {...rest}
    >
      {initial}
    </div>
  );
};

export default UserAvatar;
export { PALETTE, hashColor };
